// Build queue widget — the two gates between "RM submitted" and "on the
// calendar" (Ship 3, Aug 26 2026).
//
//   build_submitted            → Chad or Robert approves (/dispatch · approvals)
//   schedule_build + no date   → Robert schedules        (/dispatch robert · unscheduled)
//
// Both rows render for both people because it is ONE handoff — Chad wants to
// see whether approvals actually landed on a calendar, and Robert wants to see
// what is about to arrive. The `schedule_build` predicate is the exact one the
// Unscheduled tab uses, so the counts reconcile with the surface each row
// opens. Deep links carry an explicit `view=`: since Oct 22, 2026 Build
// Approvals is on Chad's AND Robert's views (Robert approves too), so the
// approve row opens the viewer's own view; the Schedule tab lives on Robert's.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../context/AuthContext";
import { buildApprovalsLink } from "../../../lib/buildApproval";
import { daysSince } from "../../../lib/dealAge";
import { logCardClick } from "../../../lib/uiEvents";

const SHOW = 3;
const SCHEDULE_LINK = "/dispatch?view=robert&tab=unscheduled";

interface QueueRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  stage_entered_at: string | null;
}

interface Loaded {
  approve: QueueRow[];
  schedule: QueueRow[];
}

export default function BuildQueueWidget() {
  const { role } = useAuth();
  const approvalsLink = buildApprovalsLink(role);
  const [data, setData] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cols = "id,homeowner_name,property_street,stage_entered_at";
      const [a, s] = await Promise.all([
        supabase
          .from("deals")
          .select(cols)
          .eq("stage", "build_submitted")
          .order("stage_entered_at", { ascending: true }),
        supabase
          .from("deals")
          .select(cols)
          .eq("stage", "schedule_build")
          .is("build_scheduled_date", null)
          .order("stage_entered_at", { ascending: true }),
      ]);
      if (cancelled) return;
      // A failed read must never render as "0 waiting" — that reads as done.
      if (a.error || s.error) {
        setFailed(true);
        return;
      }
      setData({
        approve: (a.data as QueueRow[]) ?? [],
        schedule: (s.data as QueueRow[]) ?? [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const section = (
    label: string,
    sub: string,
    rows: QueueRow[],
    to: string,
    keyBase: string,
    tone: "amber" | "sky",
  ) => (
    <div className="mt-2 first:mt-0">
      <Link
        to={to}
        onClick={() => logCardClick(`${keyBase}_head`)}
        className="flex items-center gap-2"
      >
        <span className="text-[12px] font-extrabold text-bardin-ink">{label}</span>
        <span className="text-[10.5px] text-slate-400 truncate flex-1">{sub}</span>
        <span
          className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold ${
            rows.length > 0
              ? tone === "amber"
                ? "bg-amber-50 text-amber-700"
                : "bg-sky-50 text-sky-700"
              : "bg-slate-100 text-slate-400"
          }`}
        >
          {rows.length}
        </span>
      </Link>
      {rows.length === 0 ? (
        <div className="text-[11px] text-emerald-700 font-semibold py-1">Clear ✓</div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {rows.slice(0, SHOW).map((r) => {
              const d = daysSince(r.stage_entered_at);
              return (
                <Link
                  key={r.id}
                  to={`/deal/${r.id}`}
                  onClick={() => logCardClick(`${keyBase}_row`)}
                  className="flex items-center gap-2 py-1.5"
                >
                  <span className="text-[13px] font-bold text-bardin-ink truncate">
                    {r.homeowner_name ?? "—"}
                  </span>
                  <span className="text-[11px] text-slate-400 truncate flex-1">
                    {r.property_street ?? ""}
                  </span>
                  {d != null && (
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold whitespace-nowrap ${
                        d >= 7 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {d}d
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          {rows.length > SHOW && (
            <div className="text-[10px] text-slate-400 font-semibold mt-0.5">
              +{rows.length - SHOW} more
            </div>
          )}
        </>
      )}
    </div>
  );

  const total = data ? data.approve.length + data.schedule.length : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🔨</span>
        <span className="font-bold text-sm text-bardin-ink">Build Queue</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : total == null ? "…" : `${total} waiting`}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load the build queue — try a refresh.</div>
      ) : !data ? (
        <div className="space-y-2 mt-1">
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : (
        <>
          {section(
            "To approve",
            "submitted, waiting on Chad or Robert",
            data.approve,
            approvalsLink,
            "widget_buildq_approve",
            "amber",
          )}
          <div className="border-t border-slate-100 mt-2" />
          {section(
            "To schedule",
            "approved, no build date yet",
            data.schedule,
            SCHEDULE_LINK,
            "widget_buildq_schedule",
            "sky",
          )}
        </>
      )}
    </div>
  );
}
